import { useState, useEffect } from 'react';
import { Container, Stack, Title, Paper, ActionIcon, Group } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { LoadingScreen } from '../Layout/LoadingScreen';
import { usePageTitle } from '../../hooks/usePageTitle';

interface LegalPageProps {
  title: string;
  filename: string;
}

export const LegalPage = ({ title, filename }: LegalPageProps) => {
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Unique tab/SEO title per legal page (these routes aren't server-rendered,
  // so without this they'd inherit the generic landing title).
  usePageTitle(`${title} | Meals You Love`);

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`/${filename}`);
        const text = await response.text();
        setContent(text);
      } catch (error) {
        console.error(`Error loading ${title.toLowerCase()}:`, error);
        setContent(`Error loading ${title.toLowerCase()}. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [filename, title]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={handleBack}
            aria-label="Go back"
            style={{ flexShrink: 0 }}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Title order={1} size="h2">{title}</Title>
        </Group>

        <Paper p="xl" radius="lg" withBorder>
          <div 
            style={{ 
              lineHeight: 1.6,
              fontSize: '14px',
              color: 'var(--mantine-color-dark-6)'
            }}
            dangerouslySetInnerHTML={{ 
              __html: content
                // Handle date lines at the top (convert single newlines to line breaks for date section)
                .replace(/^(\*\*Effective Date:\*\*[^\n]*)\n(\*\*Last Updated:\*\*[^\n]*)/m, '$1<br>$2')
                // First handle headers to prevent them being wrapped in paragraphs
                .replace(/^### (.+)$/gm, '<h3 style="font-size: 16px; font-weight: 600; margin: 16px 0 8px 0; color: var(--mantine-color-dark-8)">$1</h3>')
                .replace(/^## (.+)$/gm, '<h2 style="font-size: 20px; font-weight: 600; margin: 16px 0 8px 0; color: var(--mantine-color-dark-8)">$1</h2>')
                .replace(/^# (.+)$/gm, '<h1 style="font-size: 24px; font-weight: 600; margin: 20px 0 12px 0; color: var(--mantine-color-dark-8)">$1</h1>')
                // Handle bold text with colons
                .replace(/^\*\*(.+?)\*\*:/gm, '<strong style="font-weight: 600; color: var(--mantine-color-dark-8)">$1:</strong>')
                // Handle other bold text
                .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 600">$1</strong>')
                // Handle markdown links
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: var(--mantine-color-orange-6); text-decoration: underline">$1</a>')
                // Handle numbered list items (convert to ordered list items)
                .replace(/^\d+\.\s+(.+)$/gm, '<oli style="margin: 2px 0">$1</oli>')
                // Handle bullet list items
                .replace(/^- (.+)$/gm, '<li style="margin: 2px 0">$1</li>')
                // Group consecutive numbered list items into ol
                .replace(/(<oli[^>]*>.*?<\/oli>)(\s*<oli[^>]*>.*?<\/oli>)*/gs, '<ol style="margin: 8px 0; padding-left: 20px">$&</ol>')
                // Group consecutive bullet list items into ul
                .replace(/(<li[^>]*>.*?<\/li>)(\s*<li[^>]*>.*?<\/li>)*/gs, '<ul style="margin: 8px 0; padding-left: 20px">$&</ul>')
                // Convert oli tags to li tags (after they've been grouped into ol)
                .replace(/<oli/g, '<li').replace(/<\/oli>/g, '</li>')
                // Convert double newlines to paragraph breaks
                .replace(/\n\n/g, '</p><p style="margin: 8px 0">')
                // Wrap the whole thing in a paragraph (will be split by the above)
                .replace(/^/, '<p style="margin: 8px 0">')
                .replace(/$/, '</p>')
                // Clean up paragraphs around headers and lists
                .replace(/<p[^>]*>\s*(<h[1-6][^>]*>)/g, '$1')
                .replace(/(<\/h[1-6]>)\s*<\/p>/g, '$1')
                .replace(/<p[^>]*>\s*(<ul[^>]*>)/g, '$1')
                .replace(/(<\/ul>)\s*<\/p>/g, '$1')
                .replace(/<p[^>]*>\s*(<strong[^>]*>)/g, '$1')
                // Remove empty paragraphs
                .replace(/<p[^>]*>\s*<\/p>/g, '')
            }}
          />
        </Paper>
      </Stack>
    </Container>
  );
};